import {
  APPLICANT_AUDIT_ENTITY_TYPES,
  type ApplicantAssessmentResultValue,
  type ApplicantAssessmentStageValue,
  type ApplicantListItem,
} from '../applicants/applicantTypes';

/** Mirrors of the C# Deliberation DTO records, field for field. */

/** One stage's outcome and the points it earned. */
export interface DeliberationStageScore {
  stage: ApplicantAssessmentStageValue;
  /** A health stage this caller may not read — every value below is then absent. */
  isHidden: boolean;
  /** Absent until recorded. */
  result: ApplicantAssessmentResultValue | null;
  resultLabel: string | null;
  /** The stage's weight when Passed, 0 when Failed. Absent until recorded, or while Score Points are unset. */
  score: number | null;
}

/** One applicant on a posting's deliberation. **No name** — resolve `applicant.personId`. */
export interface DeliberationApplicantRow {
  applicant: ApplicantListItem;
  /** Every stage, in order — recorded or not. */
  stages: DeliberationStageScore[];
  /** Absent while Score Points are unset, or while `isTotalHidden`. */
  total: number | null;
  /** A health stage is hidden from this caller, so the total is withheld with it. */
  isTotalHidden: boolean;
  /** Deliberate's own call on the applicant. False until somebody shortlists them. */
  isShortlisted: boolean;
  /** Endorsed for appointment — drives the row menu and the Endorsed filter. */
  isEndorsed: boolean;
  /**
   * The place among the posting's live applicants by TOTAL, highest first — automatic; ties share a place.
   * Absent without a total, and absent where the total is hidden (it would leak the health stages).
   */
  rank: number | null;
}

/** What the deliberation decided about one applicant. All defaults until the first save. */
export interface ApplicantDeliberation {
  isShortlisted: boolean;
  /** Endorsed for appointment — a shortlisted applicant only. */
  isEndorsed: boolean;
  /** Who endorsed — the picked account, and its display name. */
  endorsedByUserId: string | null;
  endorsedByName: string | null;
  /** The day of the endorsement. A CALENDAR date — `formatDateOnly`. */
  endorsedDate: string | null;
  /** Plain text. */
  endorsementRemarks: string | null;
  /** The endorsement document's own name; absent with no file. */
  endorsementFileName: string | null;
  /** The Schedule of Oath Taking. A CALENDAR date (`YYYY-MM-DD`) — `formatDateOnly`, never the instant path. */
  oathTakingDate: string | null;
  /** A time of day (`HH:mm:ss`) — `formatClockTime`. */
  oathTakingTime: string | null;
  oathTakingPlace: string | null;
  /** Sanitised HTML — `<RichTextDocument>` only. */
  oathTakingNotes: string | null;
  /** The planned Assignment — absent until recorded. */
  assignmentOrgUnitId: string | null;
  assignmentOrgUnitName: string | null;
  /** A CALENDAR date (`YYYY-MM-DD`). */
  assignmentEffectiveDate: string | null;
  assignmentRemarks: string | null;
  /** Sanitised HTML — render through `<RichTextDocument>` only. */
  note: string | null;
  /** Absent until the first save. */
  updatedAt: string | null;
  updatedByName: string | null;
}

/** Records or corrects it — both fields, every time; an absent note clears it. */
export interface ApplicantDeliberationRequest {
  isShortlisted: boolean;
  note: string | null;
}

/** The list filter's one value; its absence lists All. Mirrors `DeliberationFieldRules.ShortlistedFacet`. */
export const SHORTLISTED_FILTER = 'shortlisted';

/** The rail's third value. Mirrors `DeliberationFieldRules.EndorsedFacet`. */
export const ENDORSED_FILTER = 'endorsed';

/** The endorsed applicants whose APPOINTMENT is recorded. Mirrors `DeliberationFieldRules.AppointedFacet`. */
export const APPOINTED_FILTER = 'appointed';

/**
 * Deliberate's posting folders (product call, 22 Oct 2026). Mirrors `DeliberationFieldRules`'s stages, and
 * `postingStage` below is the server's classification written a second time — change both together.
 */
export const POSTING_STAGE = { Pending: 'pending', Complete: 'complete', Cancelled: 'cancelled' } as const;

/** COMPLETE = an appointment recorded, or the item filled; PENDING = open otherwise; CANCELLED = ended otherwise. */
export function postingStage(posting: { isActive: boolean; isCompleted: boolean; isFilled: boolean }) {
  if (posting.isCompleted || posting.isFilled) return POSTING_STAGE.Complete;
  return posting.isActive ? POSTING_STAGE.Pending : POSTING_STAGE.Cancelled;
}

/** Endorses a SHORTLISTED applicant, or corrects an endorsement — who, when, remarks. The file rides after. */
export interface EndorsementRequest {
  endorsedByUserId: string;
  /** `YYYY-MM-DD`. */
  endorsedDate: string;
  remarks: string | null;
}

/** The planned Assignment — unit and effective date, every time; remarks optional. Endorsed only. */
export interface AssignmentRequest {
  orgUnitId: string;
  effectiveDate: string;
  remarks: string | null;
}

/** One live unit for the Assignment's picker. */
export interface DeliberationUnitOption {
  id: string;
  name: string;
  orgUnitTypeName: string;
  parentId: string | null;
}

/** The Schedule of Oath Taking — all three, every time; an endorsed applicant only. */
export interface OathTakingRequest {
  date: string;
  /** `HH:mm:00`. */
  time: string;
  place: string;
  /** Rich text, sanitised on write; null clears it. */
  notes: string | null;
}

/** The oath-taking place's length. Mirrors `DeliberationFieldRules.OathTakingPlaceMaxLength`. */
export const OATH_TAKING_PLACE_MAX_LENGTH = 300;

/** The note's plain-text budget. Mirrors `DeliberationFieldRules.NotePlainTextMaxLength`. */
export const DELIBERATION_NOTE_MAX_LENGTH = 4000;

/** Reading Deliberate. */
export const DELIBERATION_READ_PERMISSION = 'deliberations.read';

/** Its one write — shortlisting, and the note. */
export const DELIBERATION_UPDATE_PERMISSION = 'deliberations.update';

/** Endorsing for appointment, withdrawing it, and scheduling the oath taking. */
export const DELIBERATION_ENDORSE_PERMISSION = 'deliberations.endorse';

/**
 * What Deliberate's History Logs reads: the applicant records the scores come from, and the module's
 * own record. A rename keeps the old CLR name here forever.
 */
export const DELIBERATION_AUDIT_ENTITY_TYPES = [...APPLICANT_AUDIT_ENTITY_TYPES, 'ApplicantDeliberation'];
